import React, { useEffect } from 'react';
import { BrowserRouter, Routes, Route, Navigate } from 'react-router-dom';
import { Toaster } from 'react-hot-toast';
import {useAppDispatch, useAppSelector} from './app/hooks';
import {fetchShopConfig, fetchUserShops, logout, restoreSession} from '@/features/auth';
import { saveCartBackup } from '@/features/cart/cartSlice';
import { ensurePosSession } from '@/features/session/posSession';
import { startCheckoutQueue } from '@/features/checkout/checkoutQueue';
import api from '@/services/api';
import { API_ENDPOINTS } from '@/utils/constants';
import axios from 'axios';

// Layout
import Layout from './components/layout/Layout';
import AuthGuard from './features/auth/AuthGuard';

// Pages
import LoginPage from './features/auth/LoginPage';
import ShopSelector from './features/auth/ShopSelector';
import CatalogPage from './features/catalog/CatalogPage';
import CartPage from './features/cart/CartPage';
import CheckoutPage from './features/checkout/CheckoutPage';
import { TransactionsPage } from './features/transactions';
import ErrorBoundary from './components/ui/ErrorBoundary';
import HeldCartsPage from "@/features/cart/HeldCartsPage.tsx";
import DashboardPage from "@/features/dashboard/DashboardPage";
import DailyReportPage from "@/features/reports/DailyReportPage";
import NotificationsPage from "@/features/notifications/NotificationsPage";
import OnlineOrdersPage from "@/features/onlineorders/OnlineOrdersPage";
import TopupPage from "@/features/topup/TopupPage";
import AttendancePage from "@/features/attendance/AttendancePage";
import PettyCashPage from "@/features/pettycash/PettyCashPage";
import ReimbursementPage from "@/features/reimbursement/ReimbursementPage";
import StockRequestPage from "@/features/stockrequest/StockRequestPage";
import StockOpnamePage from "@/features/stockopname/StockOpnamePage";
import SettingsPage from "@/features/settings/SettingsPage";

/**
 * Main App Component
 *
 * Features:
 * - React Router for navigation
 * - Redux state management
 * - Protected routes with AuthGuard
 * - Toast notifications
 * - Session restoration
 */

const App: React.FC = () => {
    const dispatch = useAppDispatch();
    const { isInitialized, isAuthenticated, shops, user, selectedShop } = useAppSelector((state) => state.auth);
    const { items, customerName, customerPhone, notes, totalDiscount, orderType } = useAppSelector((state) => state.cart);

    // Restore session on mount
    useEffect(() => {
        dispatch(restoreSession());
    }, [dispatch]);

    // Persist cart so it survives a reload (hydrated in cartSlice initialState)
    useEffect(() => {
        dispatch(saveCartBackup());
    }, [dispatch, items, customerName, customerPhone, notes, totalDiscount, orderType]);

    // Validate the cookie session once on startup. 401 is handled by the api interceptor
    // (refresh → redirect); 403 means the account is gone; network errors proceed optimistically.
    useEffect(() => {
        if (!isInitialized || !isAuthenticated) return;
        api.get(API_ENDPOINTS.ME).catch((err) => {
            if (axios.isAxiosError(err) && err.response?.status === 403) {
                dispatch(logout());
            }
        });
    }, [isInitialized, isAuthenticated, dispatch]);

    // Per-shop setup: config (tax, requireCustomerInfo...), POS session for push, queued-sale retry.
    // Re-runs on tab focus so an 11h+ session gets refreshed like the Android app does on resume.
    useEffect(() => {
        if (!isInitialized || !isAuthenticated || !user || !selectedShop) return;
        const shopId = selectedShop.shopId;
        dispatch(fetchShopConfig({ userId: user.usersId, shopId }));
        ensurePosSession(shopId);
        startCheckoutQueue();
        const onFocus = () => ensurePosSession(shopId);
        window.addEventListener('focus', onFocus);
        return () => window.removeEventListener('focus', onFocus);
    }, [isInitialized, isAuthenticated, user, selectedShop, dispatch]);

    // Fetch shops if authenticated but shops are empty (e.g. after refresh)
    useEffect(() => {
        if (isInitialized && isAuthenticated && shops.length === 0 && user) {
            dispatch(fetchUserShops(user.usersId));
        }
    }, [isInitialized, isAuthenticated, shops.length, user, dispatch]);

    return (
        <ErrorBoundary>
            <BrowserRouter>
                <Toaster
                    position="top-center"
                    toastOptions={{
                        duration: 3000,
                        style: {
                            background: 'var(--surface-card)',
                            color: 'var(--text-primary)',
                            padding: '12px 16px',
                            borderRadius: '14px',
                            fontWeight: 700,
                            boxShadow: 'var(--shadow-md)',
                        },
                        success: { iconTheme: { primary: '#18a06b', secondary: '#fff' } },
                        error: { iconTheme: { primary: '#dc3b3b', secondary: '#fff' } },
                    }}
                />

                <Routes>
                    {/* Public Routes */}
                    <Route path="/login" element={<LoginPage />} />

                    {/* Protected Routes */}
                    <Route
                        path="/"
                        element={
                            <AuthGuard>
                                <Layout />
                            </AuthGuard>
                        }
                    >
                        {/* Shop Selection */}
                        <Route index element={<Navigate to="/shop-select" replace />} />
                        <Route path="shop-select" element={<ShopSelector />} />

                        {/* Main Pages - Require Shop Selection */}
                        <Route path="dashboard" element={<DashboardPage />} />
                        <Route path="catalog" element={<CatalogPage />} />
                        <Route path="cart" element={<CartPage />} />
                        <Route path="checkout" element={<CheckoutPage />} />
                        <Route path="transactions" element={<TransactionsPage />} />
                        <Route path="held-carts" element={<HeldCartsPage />} />
                        <Route path="reports/daily" element={<DailyReportPage />} />
                        <Route path="notifications" element={<NotificationsPage />} />
                        <Route path="online-orders" element={<OnlineOrdersPage />} />
                        <Route path="topup" element={<TopupPage />} />
                        <Route path="attendance" element={<AttendancePage />} />
                        <Route path="petty-cash" element={<PettyCashPage />} />
                        <Route path="reimbursement" element={<ReimbursementPage />} />
                        <Route path="stock-request" element={<StockRequestPage />} />
                        <Route path="stock-opname" element={<StockOpnamePage />} />
                        <Route path="settings" element={<SettingsPage />} />
                    </Route>

                    {/* 404 - Redirect to home */}
                    <Route path="*" element={<Navigate to="/" replace />} />
                </Routes>
            </BrowserRouter>
        </ErrorBoundary>
    );
};

export default App;